import React, { ChangeEvent, useRef, useState } from "react";
import styled from "styled-components";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface FileUploadProps {
  onFileUpload: (file: File) => void;
  isUploading?: boolean;
}

const DropZone = styled.div<{ $isDragOver: boolean }>`
  border: 2px dashed ${(p) => (p.$isDragOver ? "#1976d2" : "#c0c0c0")};
  border-radius: 12px;
  padding: 36px 24px;
  text-align: center;
  cursor: pointer;
  background: ${(p) => (p.$isDragOver ? "#e3f2fd" : "#fafafa")};
  transition: all 0.2s ease;
  color: #666;
  font-size: 15px;

  &:hover {
    border-color: #90a4ae;
    background: #f5f5f5;
  }
`;

const UploadIcon = styled(CloudUploadIcon)`
  font-size: 36px !important;
  color: #9e9e9e;
  margin-bottom: 8px;
`;

const DropText = styled.p`
  margin: 0;
  font-size: 15px;
`;

const DropHint = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #999;
`;

const HiddenInput = styled.input`
  display: none;
`;

const FileList = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
`;

const FileTag = styled.span`
  display: inline-block;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload, isUploading = false }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = (files: FileList | File[]) => {
    if (isUploading) {
      return;
    }
    const arr = Array.from(files);
    arr.forEach((file) => onFileUpload(file));
    setUploadedFiles((prev) => [...prev, ...arr.map((f) => f.name)]);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  return (
    <div>
      <DropZone
        $isDragOver={isDragOver && !isUploading}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !isUploading && fileInputRef.current?.click()}
        style={{ cursor: isUploading ? "not-allowed" : "pointer", opacity: isUploading ? 0.6 : 1 }}
      >
        <UploadIcon />
        {isUploading ? (
          <DropText>Uploading…</DropText>
        ) : (
          <DropText>Drag &amp; drop Excel files here or click to browse</DropText>
        )}
        <DropHint>Accepts .xlsx and .xls files</DropHint>
      </DropZone>
      <HiddenInput
        type="file"
        accept=".xlsx,.xls"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      {uploadedFiles.length > 0 && (
        <FileList>
          {uploadedFiles.map((name, i) => (
            <FileTag key={`${name}-${i}`}>{name}</FileTag>
          ))}
        </FileList>
      )}
    </div>
  );
};

export default FileUpload;
